import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Typography, TextField, IconButton, Tooltip, Box, Button, Snackbar, Alert, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import { setPlatformEmail } from '../../services/platforms';
import { getAllConversionActions } from '../../services/google';

const PlatformCard = ({ platform, user, setUser, handleMenuSelection }) => {
    const { t } = useTranslation();
    const notificationUrl = `${process.env.REACT_APP_API_BASE_URL_PROD}purchase/${user.notificationId}/${platform.id}?${platform.queryParameters}`;
    const [email, setEmail] = useState(user.email);
    const [conversionActions, setConversionActions] = useState([]);
    const [selectedConversionAction, setSelectedConversionAction] = useState([]);
    const didFetchConversionActions = useRef(false); 

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    useEffect(() => {
        if (user?.platformIntegrations) {
            const matchingIntegration = user.platformIntegrations.find(
                (integration) => integration.platformId === platform.id
            );
    
            setEmail(matchingIntegration?.email || user.email);
            setSelectedConversionAction([])
        }

        const fetchConversionActions = async () => {
            didFetchConversionActions.current = true;
            console.log('Fetching conversion actions...');
            try {
                const { data } = await getAllConversionActions(user._id);
                console.log('All conversion actions:', data.conversionActions);
                setConversionActions(data.conversionActions);
            } catch (error) {
                console.error('Error fetching all conversion actions:', error);
                setSnackbarMessage(t('platformCard.loadConversionError'));
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
        };
    
        if (user.google?.tokens && didFetchConversionActions.current === false) {
            fetchConversionActions();     
        }
        
    }, [platform, t, user]);

    const handleCopy = () => {
        navigator.clipboard.writeText(notificationUrl).then(
            () => {
                setSnackbarMessage(t('platformCard.urlCopied'));
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
            },
            (err) => {
                console.error('Could not copy text: ', err);
            }
        );
    };

    const saveEmail = async (platformId) => {
        try {
            const response = await setPlatformEmail(email, platformId, user._id);
            if (response.updatedUser) {
                setUser(response.updatedUser);
                setSnackbarMessage(t('platformCard.emailSaved'));
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
            }
        } catch (error) {
            setSnackbarMessage(t('platformCard.emailSaveError'));
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const handleGoogleAdsConnect = () => {
        handleMenuSelection(2)
    }

    const handleTestIntegration = async () => {
        try {
            // Some Platforms use POST with body to send notifications
            if (platform.jsonBody) {
                console.log('Sending POST with body:', platform.jsonBody);
                const response = await fetch(notificationUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json', // Specify JSON format
                    },
                    body: JSON.parse(platform.jsonBody),
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
            } else {
                const response = await fetch(notificationUrl, {
                    method: 'POST'
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
            }

            // Show success message
            setSnackbarMessage(t('platformCard.sendSuccess'));
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Error:', error);
    
            // Show error message
            setSnackbarMessage(t('platformCard.sendError'));
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    function convertToUrlParams(path) {
        if (path == null) { return ""; };

        const parts = path.split("/");
        
        if (parts.length !== 4 || parts[0] !== "customers" || parts[2] !== "conversionActions") {
            throw new Error("Invalid pattern format");
        }
    
        const customerId = parts[1];
        const conversionActionId = parts[3];
    
        return `&customerId=${customerId}&conversionActionId=${conversionActionId}`;
    }

    const conversionActionComponent = () => {
        if (!user.google?.tokens) {
            return (
                <Box sx={{ marginBottom: 4 }}>
                <Typography variant="body1" sx={{ color: "#333", marginBottom: 1, fontWeight: "bold" }}>
                {t('platformCard.autoUploadTitle')}
                 </Typography>
                <Typography variant="body2" sx={{ color: "gray", marginBottom: 2 }}>
                    <Link 
                        variant="body2" 
                        onClick={handleGoogleAdsConnect} 
                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                    >
                        {t('platformCard.clickHere')}
                    </Link> 
                    &nbsp; {t('platformCard.connectGoogleInstructions')}
                </Typography>
                </Box>
            )
        }
        return (
            <>
                <Typography 
                    variant="body1" 
                    sx={{ 
                        color: "#333", 
                        marginBottom: 1, 
                        fontWeight: "bold", 
                        display: { xs: 'none', sm: 'none', md: 'block' }
                    }}>
                    {t('platformCard.selectConversion')}
                </Typography>
                <FormControl fullWidth size="small" sx={{ marginBottom: 4 }}>
                    <InputLabel>{t('platformCard.conversionAction')}</InputLabel>
                    <Select
                        value={selectedConversionAction || ""}
                        onChange={(e) => setSelectedConversionAction(e.target.value)}
                        label={t('platformCard.conversionAction')}
                    >
                        {conversionActions.length > 0 ? (
                            conversionActions.map((action) => (
                                <MenuItem key={action.id} value={action}>
                                    {action.name}
                                </MenuItem>
                            ))
                        ) : (
                            <MenuItem disabled>{t('platformCard.noActionsAvailable')}</MenuItem>
                        )}
                    </Select>
                </FormControl>
            </>
        );
    }

    return (
        <Box sx={{ padding: { xs: 2, sm: 4, md: 4 } }}>
            {/* Title */}
            <Typography variant="h6" sx={{ color: "#333", marginBottom: 1, letterSpacing: 3 }}>
                {t('platformCard.affiliate')} <span style={{ color: "black", fontWeight: "bold" }}>{platform.name.toUpperCase()}</span>
            </Typography>
            <Box sx={{ backgroundColor: "lightGray", height: '0.5px', width: '100%', marginBottom: 2}} />

            {/* Notification Email */}
            <Typography variant="body1" sx={{ color: "#333", marginBottom: 1, fontWeight: "bold" }}>
                {t('platformCard.emailAddress')}
            </Typography>
            <Typography variant="body2" sx={{ color: "#333", marginBottom: 1, fontWeight: "light" }}>
                {t('platformCard.emailHint')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <TextField
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                size='small'
                sx={{ marginBottom: 4 }}
            />
            <Button 
                variant="contained" 
                color="primary" 
                sx={{ height: "40px" }} 
                onClick={() => saveEmail(platform.id)}>
                {t('platformCard.save')}
            </Button>
            </Box>

            {/* Dropdown for Conversion Action */}
            {conversionActions.length > 0 && conversionActionComponent()}

            {/* Notification URL */}
            <Typography variant="body1" sx={{ color: "#333", marginBottom: 1, fontWeight: "bold" }}>
                {platform.name} {t('platformCard.notificationUrl')}
            </Typography>
            <Typography variant="body2" sx={{ color: "gray", marginBottom: 2 }}>
                {t('platformCard.notificationHint')}
            </Typography>
            <Box 
                sx={{ 
                    display: 'block',
                    alignItems: 'center', 
                    gap: 1 
                }}>
                <TextField
                        variant="outlined"
                        multiline
                        value={notificationUrl + convertToUrlParams(selectedConversionAction.resource_name)}
                        InputProps={{ readOnly: true }}
                        size='small'
                        fullWidth
                        minRows={2} // Ensures at least 2 rows for better visibility
                        maxRows={10} // Prevents excessive height
                    />
                    <Box sx={{ 
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        width: { xs: '100%', sm: '100%', md: 'auto' },
                        marginTop: 2,
                    }}>
                        <Tooltip title="Copy to clipboard">
                            <Button
                                variant="contained"
                                color="tertiary"
                                onClick={handleCopy}
                                startIcon={<ContentCopyIcon />}
                                sx={{ 
                                    alignItems: 'center' 
                                }}
                            >
                                {t('platformCard.copy')}
                            </Button>
                        </Tooltip>
                        <Tooltip title="Test Platform Integration">
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleTestIntegration}
                                startIcon={<LinkIcon />}
                                sx={{ 
                                    alignItems: 'center' 
                                }}
                            >
                                {t('platformCard.test')}
                            </Button>
                        </Tooltip>
                    </Box>
            </Box>

             {/* Snackbar for Notifications */}
             <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleSnackbarClose} 
                    severity={snackbarSeverity} 
                    sx={{ 
                        width: '100%', 
                        marginBottom: 4
                    }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PlatformCard;